import React, { useState } from 'react';
import { CheckCircle2, XCircle, Search, Brain, TrendingUp, TrendingDown, Users, Globe, BarChart2, AlertTriangle, ChevronDown, ChevronUp, Eye } from 'lucide-react';

const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button
            onClick={() => onApprove()}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" />
            Approva
          </button>
          <button
            onClick={() => setShowRejectModal(true)}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <XCircle className="w-4 h-4" />
            Rigetta
          </button>
          <button
            onClick={onRegenerate}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Rigenera
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="font-semibold text-gray-800 mb-3">Motivo del Rigetto</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Descrivi il motivo del rigetto..."
            />
            <div className="flex gap-3 mt-3">
              <button onClick={handleReject} disabled={!rejectReason.trim()} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm">
                Conferma Rigetto
              </button>
              <button onClick={() => setShowRejectModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const EmptyState = () => (
  <div className="h-full flex items-center justify-center">
    <div className="text-center">
      <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-500">Nessuna execution disponibile</p>
    </div>
  </div>
);

const StringList = ({ items }) => {
  if (!items || items.length === 0) return <p className="text-sm text-gray-400 italic">Nessun elemento</p>;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
          {typeof item === 'string' ? item : JSON.stringify(item)}
        </li>
      ))}
    </ul>
  );
};

const FrequencyBadge = ({ value }) => {
  const colors = {
    'Very High': 'bg-red-100 text-red-700',
    'High': 'bg-orange-100 text-orange-700',
    'Medium': 'bg-yellow-100 text-yellow-700',
    'Low': 'bg-gray-100 text-gray-600',
  };
  const cls = colors[value] || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{value || '—'}</span>;
};

const StrengthBadge = ({ value }) => {
  const colors = {
    'Very Strong': 'bg-purple-100 text-purple-700',
    'Strong': 'bg-blue-100 text-blue-700',
    'Medium': 'bg-sky-100 text-sky-700',
    'Weak': 'bg-gray-100 text-gray-500',
  };
  const cls = colors[value] || 'bg-gray-100 text-gray-500';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{value || '—'}</span>;
};

const VisibilityBadge = ({ value }) => {
  const colors = { low: 'bg-green-100 text-green-700', medium: 'bg-yellow-100 text-yellow-700', high: 'bg-red-100 text-red-700' };
  const cls = colors[value?.toLowerCase()] || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{value || '—'}</span>;
};

const CollapsibleSection = ({ title, icon: Icon, color = 'blue', children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const colorMap = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-600' },
    violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', icon: 'text-violet-600' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'text-orange-600' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', icon: 'text-teal-600' },
  };
  const c = colorMap[color] || colorMap.blue;
  return (
    <div className={`bg-white border ${c.border} rounded-lg overflow-hidden`}>
      <button onClick={() => setOpen(!open)} className={`w-full flex items-center justify-between p-4 ${open ? c.bg : 'hover:bg-gray-50'} transition-colors`}>
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${c.icon}`} />}
          <span className={`font-semibold text-sm ${c.text}`}>{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="p-4 border-t border-gray-100 space-y-3">{children}</div>}
    </div>
  );
};

const Contract01bView = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [expandedObs, setExpandedObs] = useState(null);

  if (!execution) return <EmptyState />;

  const output = execution.output || execution.outputData || {};
  const library = output.consumer_observation_library || [];
  const clusters = output.observation_clusters || [];
  const dominantObs = output.dominant_observations || [];
  const priorMap = output.prior_map || [];
  const secondOrderPriorMap = output.second_order_prior_map || [];
  const communicativeIntentionMap = output.communicative_intention_map || [];
  const higherOrderInference = output.higher_order_inference_model || {};
  const complaints = output.top_recurring_complaints || [];
  const positives = output.top_positive_experiences || [];
  const expectations = output.market_expectations || [];
  const frustrations = output.market_frustrations || [];
  const desires = output.market_desires || [];
  const community = output.community_differences || [];
  const meta = output.retrieval_metadata || {};
  const regional = output.regional_identity_model || {};

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-800">Contract 1B — Consumer Research Engine</h2>
          <p className="text-sm text-gray-500 mt-1">
            Ricerca OSINT delle conversazioni spontanee dei consumatori nel mercato target.
          </p>
        </div>

        {/* Review Gate */}
        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />

        {/* Observation Library */}
        {library.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-blue-600" />
              <h3 className="font-semibold text-gray-800">Consumer Observation Library ({library.length})</h3>
            </div>
            <div className="space-y-3">
              {library.map((obs, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{obs.observation}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <FrequencyBadge value={obs.frequency} />
                        <StrengthBadge value={obs.prior_strength} />
                        {obs.emotional_tone?.map((t, j) => (
                          <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{t}</span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedObs(expandedObs === i ? null : i)}
                      className="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0"
                    >
                      {expandedObs === i ? 'Chiudi' : 'Dettagli'}
                    </button>
                  </div>
                  {obs.generated_prior && (
                    <p className="text-xs text-purple-700 mt-2 italic">Prior: {obs.generated_prior}</p>
                  )}
                  {expandedObs === i && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 text-xs text-gray-600">
                      {obs.typical_context && <p><span className="font-medium">Contesto:</span> {obs.typical_context}</p>}
                      {obs.prior_protection_function && <p><span className="font-medium">Funzione protettiva:</span> {obs.prior_protection_function}</p>}
                      {obs.latent_state_explained_by_prior && <p><span className="font-medium">Latent state:</span> {obs.latent_state_explained_by_prior}</p>}
                      {obs.representative_user_expressions?.length > 0 && (
                        <div>
                          <p className="font-medium mb-1">Espressioni utenti:</p>
                          <StringList items={obs.representative_user_expressions} />
                        </div>
                      )}
                      {/* Second-Order fields from geographic_origin */}
                      {obs.geographic_origin?.second_order_prior && (
                        <div className="mt-2 pt-2 border-t border-violet-100">
                          <p className="font-medium text-violet-700 mb-1">Second-Order Prior</p>
                          <p className="italic text-violet-800">{obs.geographic_origin.second_order_prior}</p>
                          {obs.geographic_origin.higher_order_policy_response && (
                            <p className="mt-1"><span className="font-medium">Policy response:</span> {obs.geographic_origin.higher_order_policy_response}</p>
                          )}
                          {obs.geographic_origin.credibility_effect && (
                            <p><span className="font-medium">Credibility effect:</span> {obs.geographic_origin.credibility_effect}</p>
                          )}
                          {obs.geographic_origin.inferred_communicative_intentions?.length > 0 && (
                            <div className="flex gap-1 flex-wrap mt-1">
                              {obs.geographic_origin.inferred_communicative_intentions.map((int, j) => (
                                <span key={j} className="px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded text-xs">{int}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prior Map */}
        {priorMap.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-4 h-4 text-purple-600" />
              <h3 className="font-semibold text-gray-800">Prior Map ({priorMap.length})</h3>
            </div>
            <div className="space-y-2">
              {priorMap.map((p, i) => (
                <div key={i} className="border border-purple-100 bg-purple-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-purple-900">{p.prior || p}</p>
                  {(p.frequency || p.strength) && (
                    <div className="flex gap-2 mt-1">
                      {p.frequency && <FrequencyBadge value={p.frequency} />}
                      {p.strength && <StrengthBadge value={p.strength} />}
                    </div>
                  )}
                  {p.supporting_observations?.length > 0 && (
                    <div className="mt-2">
                      <StringList items={p.supporting_observations} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Observation Clusters */}
        {clusters.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart2 className="w-4 h-4 text-indigo-600" />
              <h3 className="font-semibold text-gray-800">Observation Clusters</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {clusters.map((c, i) => (
                <span key={i} className="text-sm px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                  {typeof c === 'string' ? c : c.theme || JSON.stringify(c)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Market Dynamics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {expectations.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <h3 className="font-semibold text-gray-800 text-sm">Aspettative</h3>
              </div>
              <StringList items={expectations} />
            </div>
          )}
          {frustrations.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                <h3 className="font-semibold text-gray-800 text-sm">Frustrazioni</h3>
              </div>
              <StringList items={frustrations} />
            </div>
          )}
          {desires.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <h3 className="font-semibold text-gray-800 text-sm">Desideri</h3>
              </div>
              <StringList items={desires} />
            </div>
          )}
        </div>

        {/* Top Complaints / Positives */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {complaints.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <h3 className="font-semibold text-gray-800">Top Complaints</h3>
              </div>
              <StringList items={complaints} />
            </div>
          )}
          {positives.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <h3 className="font-semibold text-gray-800">Top Positive Experiences</h3>
              </div>
              <StringList items={positives} />
            </div>
          )}
        </div>

        {/* Dominant Observations */}
        {dominantObs.length > 0 && (
          <CollapsibleSection title={`Dominant Observations (${dominantObs.length})`} icon={Eye} color="orange">
            <div className="space-y-2">
              {dominantObs.map((d, i) => (
                <div key={i} className="border border-orange-100 rounded-lg p-3 bg-orange-50/40">
                  <div className="flex items-start gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 text-xs rounded font-medium flex-shrink-0 ${d.dominance_score === 'Very High' ? 'bg-red-100 text-red-700' : d.dominance_score === 'High' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {d.dominance_score}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{d.causal_role}</span>
                  </div>
                  <p className="text-sm text-gray-800">{d.observation}</p>
                  {d.supported_priors?.length > 0 && (
                    <p className="text-xs text-orange-600 mt-1">Priors: {d.supported_priors.join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Second-Order Prior Map */}
        {secondOrderPriorMap.length > 0 && (
          <CollapsibleSection title={`Second-Order Prior Map (${secondOrderPriorMap.length})`} icon={Brain} color="violet">
            <div className="space-y-2">
              {secondOrderPriorMap.map((p, i) => (
                <div key={i} className="border border-violet-100 bg-violet-50/40 rounded-lg p-3">
                  <p className="text-sm font-medium text-violet-900 italic mb-1">"{p.second_order_prior}"</p>
                  <div className="flex gap-2 mb-1">
                    {p.frequency && <FrequencyBadge value={p.frequency} />}
                    {p.strength && <StrengthBadge value={p.strength} />}
                  </div>
                  {p.higher_order_policy_response && (
                    <p className="text-xs text-violet-700"><span className="font-medium">Policy:</span> {p.higher_order_policy_response}</p>
                  )}
                  {p.credibility_effect && (
                    <p className="text-xs text-gray-600"><span className="font-medium">Credibility:</span> {p.credibility_effect}</p>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Communicative Intention Map */}
        {communicativeIntentionMap.length > 0 && (
          <CollapsibleSection title="Communicative Intention Map" icon={Eye} color="teal">
            <div className="space-y-2">
              {communicativeIntentionMap.map((item, i) => (
                <div key={i} className="border border-teal-100 rounded-lg p-3 bg-teal-50/40">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-teal-800">{item.intention}</span>
                    {item.prevalence && <FrequencyBadge value={item.prevalence} />}
                  </div>
                  {item.consumer_response && (
                    <p className="text-xs text-gray-700"><span className="font-medium">Response:</span> {item.consumer_response}</p>
                  )}
                  {item.credibility_preservation && (
                    <p className="text-xs text-teal-600 mt-0.5"><span className="font-medium">Credibility:</span> {item.credibility_preservation}</p>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Higher-Order Inference Model */}
        {higherOrderInference && Object.keys(higherOrderInference).length > 0 && (
          <CollapsibleSection title="Higher-Order Inference Model" icon={Brain} color="violet">
            {[
              { key: 'second_order_priors', label: 'Second-Order Priors', color: 'text-violet-600' },
              { key: 'expected_communicative_intentions', label: 'Expected Communicative Intentions', color: 'text-violet-600' },
              { key: 'manipulation_detection_triggers', label: 'Manipulation Detection Triggers', color: 'text-red-600' },
              { key: 'commercial_intent_triggers', label: 'Commercial Intent Triggers', color: 'text-orange-600' },
              { key: 'strategic_selection_triggers', label: 'Strategic Selection Triggers', color: 'text-orange-600' },
              { key: 'credibility_discount_triggers', label: 'Credibility Discount Triggers', color: 'text-red-600' },
              { key: 'autonomy_protection_patterns', label: 'Autonomy Protection Patterns', color: 'text-green-600' },
              { key: 'verification_preferences', label: 'Verification Preferences', color: 'text-green-600' },
              { key: 'evidence_independence_requirements', label: 'Evidence Independence Requirements', color: 'text-blue-600' },
              { key: 'higher_order_robustness_requirements', label: 'Higher-Order Robustness Requirements', color: 'text-blue-600' },
            ].map(({ key, label, color }) => {
              if (key === 'persuasion_awareness_baseline') return null;
              const items = higherOrderInference[key];
              if (!items?.length) return null;
              return (
                <div key={key}>
                  <p className={`text-xs font-semibold ${color} uppercase tracking-wide mb-1`}>{label}</p>
                  <StringList items={items} />
                </div>
              );
            })}
            {higherOrderInference.persuasion_awareness_baseline && (
              <div>
                <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-1">Persuasion Awareness Baseline</p>
                <p className="text-sm text-gray-700">{higherOrderInference.persuasion_awareness_baseline}</p>
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* Community Differences */}
        {community.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-teal-600" />
              <h3 className="font-semibold text-gray-800">Community Differences</h3>
            </div>
            <div className="space-y-3">
              {community.map((c, i) => (
                <div key={i} className="border border-teal-100 bg-teal-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-teal-800">{c.community || JSON.stringify(c)}</p>
                  {c.unique_observations?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-teal-700 mb-1">Osservazioni uniche:</p>
                      <StringList items={c.unique_observations} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regional Identity Model */}
        {regional && Object.keys(regional).length > 0 && regional.geographic_unit && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-sky-600" />
              <h3 className="font-semibold text-gray-800">Regional Identity Model — {regional.geographic_unit}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {regional.regional_visual_codes?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Visual Codes</p>
                  <StringList items={regional.regional_visual_codes} />
                </div>
              )}
              {regional.regional_behavioral_codes?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Behavioral Codes</p>
                  <StringList items={regional.regional_behavioral_codes} />
                </div>
              )}
              {regional.regional_linguistic_codes?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Linguistic Codes</p>
                  <StringList items={regional.regional_linguistic_codes} />
                </div>
              )}
              {regional.regional_social_norms?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Social Norms</p>
                  <StringList items={regional.regional_social_norms} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Retrieval Metadata */}
        {meta && Object.keys(meta).length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-2 text-sm">Retrieval Metadata</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {meta.coverage_ratio && (
                <div>
                  <p className="text-gray-500">Coverage</p>
                  <p className="font-medium text-gray-800">{meta.coverage_ratio}</p>
                </div>
              )}
              {meta.geographical_distribution?.coverage_quality && (
                <div>
                  <p className="text-gray-500">Geo Quality</p>
                  <p className="font-medium text-gray-800">{meta.geographical_distribution.coverage_quality}</p>
                </div>
              )}
              {meta.successful_sources?.length > 0 && (
                <div className="col-span-2">
                  <p className="text-gray-500">Fonti OK</p>
                  <p className="font-medium text-gray-800">{meta.successful_sources.join(', ')}</p>
                </div>
              )}
            </div>
            {meta.limitations?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">Limitazioni:</p>
                <StringList items={meta.limitations} />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Contract01bView;
